/**
 * The prioritized patching schedule.
 *
 * The ranking on the Fix First screen says what to do; this screen is where that becomes work
 * somebody owns, in a window somebody agreed to. Two numbers carry the screen, and they answer
 * different questions:
 *
 *   **Projected** is what the campaign was expected to remove *when it was planned*, recorded at
 *   that moment. **Open** is what is still open across the same findings today.
 *
 * Keeping the projection rather than recomputing it is the whole point. An estate moves for
 * reasons that have nothing to do with whether this campaign was done — scans re-run, assets are
 * decommissioned, the methodology changes — so a progress number computed only from the current
 * state can improve while nobody touches the work. Read against a fixed projection, it cannot.
 */

import { useCallback, useState, type JSX, type SyntheticEvent } from 'react';

import { api, type Campaign, type CampaignState } from '../shared/api/client';
import {
  Badge,
  Card,
  ErrorBox,
  Formula,
  PageHeader,
  Spinner,
  Stat,
  StatRow,
  Table,
  type Tone,
} from '../shared/ui/ui';
import { useAsync } from '../shared/ui/useAsync';

/** The states an operator can move a campaign to from where it is now. */
const NEXT_STATE: Record<CampaignState, CampaignState[]> = {
  planned: ['scheduled', 'canceled'],
  scheduled: ['in_progress', 'planned', 'canceled'],
  in_progress: ['completed', 'canceled'],
  completed: [],
  canceled: ['planned'],
};

const STATE_TONE: Record<CampaignState, Tone> = {
  planned: 'muted',
  scheduled: 'info',
  in_progress: 'warn',
  completed: 'ok',
  canceled: 'muted',
};

export function Campaigns(): JSX.Element {
  // Bumped after any write so the list refetches. The server owns the numbers — the projection is
  // recomputed there when findings are added — so re-reading is more honest than patching state
  // locally and hoping the two agree.
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => {
    setRevision((r) => r + 1);
  }, []);

  const { data, error, loading } = useAsync(() => api.campaigns(100), [revision]);
  const [writeError, setWriteError] = useState<unknown>(null);

  const campaigns = data ?? [];
  const active = campaigns.filter((c) => c.state === 'scheduled' || c.state === 'in_progress');
  const scheduledRisk = active.reduce((sum, c) => sum + c.open_risk, 0);
  const unassigned = campaigns.filter(
    (c) => c.owner_group === null && c.state !== 'completed' && c.state !== 'canceled',
  ).length;

  const move = (id: string, state: CampaignState): void => {
    setWriteError(null);
    void api
      .setCampaignState(id, state)
      .then(refresh)
      .catch((e: unknown) => {
        setWriteError(e);
      });
  };

  return (
    <>
      <PageHeader
        title="Patching schedule"
        subtitle="Ranked risk becomes work somebody owns, in a window somebody agreed to."
      />

      <ErrorBox error={error} />
      <ErrorBox error={writeError} />

      <Card>
        <StatRow>
          <Stat label="Campaigns" value={campaigns.length} />
          <Stat
            label="In flight"
            value={active.length}
            hint="scheduled or under way"
            tone={active.length > 0 ? 'info' : 'neutral'}
          />
          <Stat
            label="Risk in flight"
            value={scheduledRisk}
            hint="still open across those campaigns"
          />
          <Stat
            label="Unassigned"
            value={unassigned}
            tone={unassigned > 0 ? 'warn' : 'neutral'}
            hint="no owner group — nobody has picked it up"
          />
        </StatRow>
      </Card>

      <PlanCampaign onPlanned={refresh} />

      <Card
        title="Scheduled work"
        subtitle="Soonest window first. Projected is what the campaign was expected to remove when planned; open is what is left today."
      >
        {loading ? (
          <Spinner />
        ) : (
          <Table
            columns={['Campaign', 'Owner', 'Window', 'Findings', 'Projected', 'Open', '']}
            empty="No campaigns yet. Plan one above, then add findings from Fix First."
          >
            {campaigns.map((c) => (
              <tr key={c.id}>
                <td>
                  <div className="table__strong">{c.name}</div>
                  <Badge tone={STATE_TONE[c.state]}>{c.state.replace('_', ' ')}</Badge>
                </td>
                <td>
                  {c.owner_group ?? (
                    <Badge tone="warn">unassigned</Badge>
                  )}
                </td>
                <td>
                  <Formula>{formatWindow(c)}</Formula>
                </td>
                <td className="table__num">{c.finding_count}</td>
                <td className="table__num">{c.projected_risk_reduction ?? '—'}</td>
                <td className="table__num table__strong">{c.open_risk}</td>
                <td>
                  {NEXT_STATE[c.state].map((next) => (
                    <button
                      key={next}
                      type="button"
                      className="button button--small"
                      onClick={() => {
                        move(c.id, next);
                      }}
                    >
                      {next.replace('_', ' ')}
                    </button>
                  ))}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}

/**
 * Plans a campaign.
 *
 * The owner is a **group**, not a person, and the field says so. A campaign assigned to somebody
 * who has left, changed team, or is on leave stalls silently; a group always has somebody in it.
 */
function PlanCampaign({ onPlanned }: { onPlanned: () => void }): JSX.Element {
  const [name, setName] = useState('');
  const [ownerGroup, setOwnerGroup] = useState('');
  const [windowStart, setWindowStart] = useState('');
  const [windowEnd, setWindowEnd] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    setError(null);
    setBusy(true);

    void api
      .createCampaign({
        name: name.trim(),
        owner_group: ownerGroup.trim() === '' ? null : ownerGroup.trim(),
        window_start: toRfc3339(windowStart),
        window_end: toRfc3339(windowEnd),
      })
      .then(() => {
        setName('');
        setOwnerGroup('');
        setWindowStart('');
        setWindowEnd('');
        onPlanned();
      })
      .catch((e: unknown) => {
        setError(e);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Card
      title="Plan a campaign"
      subtitle="A campaign cannot be scheduled without a window — the schema refuses it, so 'scheduled' cannot mean 'scheduled for nothing'."
    >
      <ErrorBox error={error} />
      <form className="inline-form" onSubmit={submit}>
        <input
          className="input"
          placeholder="Name, e.g. August edge firmware"
          value={name}
          maxLength={256}
          required
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
        <input
          className="input"
          placeholder="Owner group"
          value={ownerGroup}
          onChange={(e) => {
            setOwnerGroup(e.target.value);
          }}
        />
        <input
          className="input"
          type="datetime-local"
          aria-label="Window start"
          value={windowStart}
          onChange={(e) => {
            setWindowStart(e.target.value);
          }}
        />
        <input
          className="input"
          type="datetime-local"
          aria-label="Window end"
          value={windowEnd}
          onChange={(e) => {
            setWindowEnd(e.target.value);
          }}
        />
        <button type="submit" className="button" disabled={busy || name.trim() === ''}>
          {busy ? 'Planning…' : 'Plan'}
        </button>
      </form>
    </Card>
  );
}

/**
 * `datetime-local` yields a local wall-clock string with no zone. Converting through `Date` stamps
 * the browser's offset, which is what the operator meant: they typed the time they will be in the
 * room, not a UTC instant.
 */
function toRfc3339(local: string): string | null {
  if (local === '') return null;
  const parsed = new Date(local);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function formatWindow(c: Campaign): string {
  if (c.window_start === null) return 'no window set';
  const start = new Date(c.window_start);
  const end = c.window_end === null ? null : new Date(c.window_end);
  // ISO 8601 dates, per the house style — US/international ambiguity is not worth the risk on a
  // screen whose whole purpose is telling somebody which night they are working.
  const day = start.toISOString().slice(0, 10);
  const from = start.toISOString().slice(11, 16);
  if (end === null) return `${day} from ${from}`;
  return `${day} ${from}–${end.toISOString().slice(11, 16)} UTC`;
}
